import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api';
import { FormField } from '@/components/FormField';
import { Spinner } from '@/components/Spinner';
import { US_STATES } from '@/lib/reference';
import type { AccountUpdateValues } from '@/types/api';

const schema = z
  .object({
    email: z.string().min(1, 'Email is required').email('Enter a valid email'),
    password: z.string(),
    confirmPassword: z.string(),
    name: z.string(),
    address: z.string(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
  })
  .superRefine((val, ctx) => {
    // Mirrors account.php: only validate when a new password was entered.
    if (val.password && val.password !== val.confirmPassword) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['confirmPassword'], message: 'Passwords do not match' });
    }
  });
type Values = z.infer<typeof schema>;

const EMPTY: Values = { email: '', password: '', confirmPassword: '', name: '', address: '', city: '', state: 'OK', zip: '' };

export default function AccountPage() {
  const [banner, setBanner] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const queryClient = useQueryClient();
  const { data: account, isLoading } = useQuery({ queryKey: ['account'], queryFn: () => api.getAccount() });

  const {
    register, handleSubmit, reset,
    formState: { errors, isSubmitting },
  } = useForm<Values>({ resolver: zodResolver(schema), defaultValues: EMPTY });

  useEffect(() => {
    if (account) reset({ ...EMPTY, ...account });
  }, [account, reset]);

  const onSubmit = async (values: Values) => {
    setBanner(null);
    const res = await api.updateAccount(values as AccountUpdateValues);
    if (res.err === '1') {
      setBanner({ kind: 'ok', text: 'Update successful.' });
      // Refresh the cached account so the change shows without a manual refresh.
      await queryClient.invalidateQueries({ queryKey: ['account'] });
      // Clear the password fields after a successful save.
      reset({ ...values, password: '', confirmPassword: '' });
    } else {
      setBanner({ kind: 'err', text: typeof res.message === 'string' ? res.message : 'An error occurred.' });
    }
  };

  if (isLoading) {
    return <div className="ast-cpl text-center py-5"><Spinner /></div>;
  }

  return (
    <div className="ast-cpl ast-account">
      <div className="ast-page-head">
        <h2 className="serif ast-page-title">Account Info</h2>
      </div>

      {banner && (
        <div className={`alert ${banner.kind === 'ok' ? 'alert-success' : 'alert-danger'} py-2`} role="alert">
          {banner.text}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* User Info */}
        <section className="ast-card ast-form-section">
          <h3 className="ast-section-title">User Info</h3>
          <FormField label="Email" htmlFor="acct-email" error={errors.email?.message}>
            <input id="acct-email" type="email" className="form-control form-control-sm" {...register('email')} />
          </FormField>
          <div className="ast-grid-2 mt-2">
            <FormField label="New Password" htmlFor="acct-password">
              <input id="acct-password" type="password" autoComplete="new-password" className="form-control form-control-sm" {...register('password')} />
            </FormField>
            <FormField label="Confirm New Password" htmlFor="acct-confirm" error={errors.confirmPassword?.message}>
              <input id="acct-confirm" type="password" autoComplete="new-password" className="form-control form-control-sm" {...register('confirmPassword')} />
            </FormField>
          </div>
          <div className="ast-field-hint">Leave the password fields blank to keep your current password.</div>
        </section>

        {/* Agent Info */}
        <section className="ast-card ast-form-section">
          <h3 className="ast-section-title">Agent Info</h3>
          <FormField label="Name" htmlFor="acct-name">
            <input id="acct-name" className="form-control form-control-sm" {...register('name')} />
          </FormField>
          <FormField label="Address" htmlFor="acct-address" className="mt-2">
            <input id="acct-address" className="form-control form-control-sm" {...register('address')} />
          </FormField>
          <div className="ast-grid-2 mt-2">
            <FormField label="City" htmlFor="acct-city">
              <input id="acct-city" className="form-control form-control-sm" {...register('city')} />
            </FormField>
            <FormField label="State" htmlFor="acct-state">
              <select id="acct-state" className="form-select form-select-sm" {...register('state')}>
                {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
              </select>
            </FormField>
            <FormField label="Zip" htmlFor="acct-zip">
              <input id="acct-zip" className="form-control form-control-sm" maxLength={10} {...register('zip')} />
            </FormField>
          </div>
        </section>

        <div className="ast-form-actions">
          <button type="submit" className="btn btn-ast-blue" disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : 'Submit'}
          </button>
        </div>
      </form>
    </div>
  );
}
